// src/screens/KioskCustomerInfoScreen.js
import React, { useContext, useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { AuthContext } from '../context/AuthContext';
import AppHeader from '../components/AppHeader';
import useKioskInactivity from '../hooks/useKioskInactivity';
import { useKioskDraft } from '../context/KioskDraftContext';

const KioskCustomerInfoScreen = () => {
  const { user } = useContext(AuthContext);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { draft, updateDraft } = useKioskDraft();
  useKioskInactivity();

  const vehicleId = searchParams.get('vehicle');
  // Coming back from the next step keeps what this customer typed.
  const sameWalkIn = draft.vehicleId === vehicleId;
  const [customerName, setCustomerName] = useState(sameWalkIn ? draft.customerName || '' : '');
  const [customerEmail, setCustomerEmail] = useState(sameWalkIn ? draft.customerEmail || '' : '');
  const [customerPhone, setCustomerPhone] = useState(sameWalkIn ? draft.customerPhone || '' : '');
  const [error, setError] = useState('');

  useEffect(() => {
    if (!user || user.role !== 'kiosk') {
      navigate('/login');
      return;
    }

    if (!vehicleId) {
      navigate('/kiosk/book');
      return;
    }
  }, [user, vehicleId, navigate]);

  const handleSubmit = (e) => {
    e.preventDefault();
    setError('');

    // Basic validation
    if (!customerName.trim()) {
      setError('Please enter customer name');
      return;
    }

    if (!customerEmail.trim()) {
      setError('Please enter customer email');
      return;
    }

    // Email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(customerEmail)) {
      setError('Please enter a valid email address');
      return;
    }

    if (!customerPhone.trim()) {
      setError('Please enter customer phone number');
      return;
    }

    // Personal details stay in memory, never in the URL (browser history).
    updateDraft({ vehicleId, customerName, customerEmail, customerPhone });
    navigate('/kiosk/book/select-slot');
  };

  return (
    <div className="flex flex-col h-full">
      <AppHeader title="Customer Information" subtitle="Enter customer details" />

      <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto p-4 pb-20 space-y-4">
        {error && (
          <div className="bg-red-500/10 border border-red-500/30 rounded-xl p-3">
            <p className="text-red-400 text-xs">{error}</p>
          </div>
        )}

        <div>
          <label className="text-xs text-app-text-secondary-light dark:text-app-text-secondary">Customer Name</label>
          <input
            type="text"
            className="mt-1 w-full px-3 py-2 rounded-xl bg-app-input-light dark:bg-app-input border border-app-input-border-light dark:border-app-input-border text-sm outline-none"
            value={customerName}
            onChange={(e) => setCustomerName(e.target.value)}
            placeholder="John Doe"
            required
          />
        </div>

        <div>
          <label className="text-xs text-app-text-secondary-light dark:text-app-text-secondary">Email Address</label>
          <input
            type="email"
            className="mt-1 w-full px-3 py-2 rounded-xl bg-app-input-light dark:bg-app-input border border-app-input-border-light dark:border-app-input-border text-sm outline-none"
            value={customerEmail}
            onChange={(e) => setCustomerEmail(e.target.value)}
            placeholder="john@example.com"
            required
          />
        </div>

        <div>
          <label className="text-xs text-app-text-secondary-light dark:text-app-text-secondary">Phone Number</label>
          <input
            type="tel"
            className="mt-1 w-full px-3 py-2 rounded-xl bg-app-input-light dark:bg-app-input border border-app-input-border-light dark:border-app-input-border text-sm outline-none"
            value={customerPhone}
            onChange={(e) => setCustomerPhone(e.target.value)}
            placeholder="(555) 123-4567"
            required
          />
        </div>

        <div className="flex gap-3 pt-4">
          <button
            type="button"
            onClick={() => navigate('/kiosk/book')}
            className="flex-1 py-3 rounded-2xl bg-app-bg-secondary-light dark:bg-app-bg-secondary text-app-text-light dark:text-app-text text-sm font-semibold hover:opacity-90"
          >
            Back
          </button>

          <button
            type="submit"
            className="flex-1 py-3 rounded-2xl bg-app-accent text-black text-sm font-semibold hover:opacity-90"
          >
            Continue
          </button>
        </div>
      </form>
    </div>
  );
};

export default KioskCustomerInfoScreen;
